import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ResolveWordmark from "../components/ResolveWordmark";

export default function ForgotPassword() {
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!loading && user) return <Navigate to="/cases" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim().toLowerCase(),
      });
      setDone(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        toast.error(
          "Too many reset attempts. Please wait an hour and try again.",
        );
      } else {
        // Even on errors we show the neutral success message so we never
        // leak whether the email exists.
        setDone(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-brand-page" data-testid="forgot-password-page">
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-72 navy-curve"
        aria-hidden="true"
      />
      <div className="relative z-10 mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-4 py-10">
        <div className="mb-6 flex flex-col items-center gap-2">
          <img
            src="/brand/goodwin-resolve-logo.png"
            alt="Goodwin Resolve"
            className="h-16 w-16 object-contain"
          />
          <ResolveWordmark size="lg" scheme="navy" />
        </div>

        <div className="brand-card w-full p-7 sm:p-8">
          {!done ? (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-[color:var(--page-fg)]">
                Forgot your password?
              </h1>
              <p className="mt-1 text-sm text-[color:var(--page-fg-secondary)]">
                Enter your work email and we'll send you a reset link if an
                account exists.
              </p>
              <form onSubmit={submit} className="mt-5 space-y-4" data-testid="forgot-password-form">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@goodwinliving.org"
                    className="h-11 rounded-xl border-[color:var(--border-subtle)] bg-white"
                    data-testid="forgot-password-email"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="h-11 w-full rounded-xl font-semibold text-white"
                  style={{ backgroundColor: "#1E3A5F" }}
                  data-testid="forgot-password-submit"
                >
                  {submitting ? "Sending…" : "Send reset link"}
                </Button>
                <Link
                  to="/login"
                  className="block text-center text-sm font-medium text-brand-navy hover:text-brand-green hover:underline"
                  data-testid="forgot-back-to-login"
                >
                  <ArrowLeft className="mr-1 inline h-4 w-4" />
                  Back to sign in
                </Link>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-password-success">
              <MailCheck className="mx-auto h-12 w-12" style={{ color: "#5EAB45" }} />
              <h1 className="mt-3 text-xl font-semibold text-[color:var(--page-fg)]">
                Check your inbox
              </h1>
              <p className="mt-2 text-sm text-[color:var(--page-fg-secondary)]">
                If an account exists for this email, a reset link has been sent.
                The link is valid for 60 minutes and can be used once.
              </p>
              <Link
                to="/login"
                className="mt-5 inline-flex items-center text-sm font-medium text-brand-navy hover:text-brand-green hover:underline"
                data-testid="forgot-success-back"
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
